import type { SnEvent } from './types'

/** A breadcrumb as `Page`/`PageHeader` consume it. */
export interface EventBreadcrumb {
  id: string
  /** Full path. An EMPTY string renders the crumb as non-clickable text. */
  to: string
  text: string
}

interface BuildEventBreadcrumbsArgs {
  event?: SnEvent | null
  /** Route root for an organization page, i.e. ROUTES.ORG_ROOT. */
  orgRoot: string
  /** Route root for a competition page, i.e. ROUTES.SCHEDULE_ROOT. */
  scheduleRoot: string
  /** Route root for a team page, i.e. ROUTES.TEAM_ROOT. */
  teamRoot: string
}

/** `name`, falling back to `name_full`. */
const nameOf = (
  entity?: { name?: string | null; name_full?: string | null } | null
): string => entity?.name?.trim() || entity?.name_full?.trim() || ''

/**
 * Breadcrumb trail above the event title: organization > competition > team.
 *
 * Built entirely from the event payload's own `organization`, `root_schedule`
 * and `team` — no request. Any of the three may be absent (a team-only event
 * has no `root_schedule`; the LIST response omits all of them), and each is
 * simply skipped, so the trail is as complete as the data allows.
 *
 * Each crumb carries its OWN route. The page must pass `useCrumbPaths` to
 * `Page`, or the shared header rebuilds every link as `/org/{id}` — its
 * long-standing default, kept for the trails that rely on it.
 *
 * The event itself is deliberately NOT a crumb: its name is already the page
 * title directly beneath the trail.
 */
export const buildEventBreadcrumbs = ({
  event,
  orgRoot,
  scheduleRoot,
  teamRoot,
}: BuildEventBreadcrumbsArgs): EventBreadcrumb[] => {
  if (!event) {
    return []
  }

  const crumbs: EventBreadcrumb[] = []

  const org = event.organization
  const orgName = nameOf(org)
  if (org?.id && orgName) {
    crumbs.push({ id: org.id, to: `${orgRoot}/${org.id}`, text: orgName })
  }

  const competition = event.root_schedule
  const competitionName = nameOf(competition)
  if (competition?.id && competitionName) {
    crumbs.push({
      id: competition.id,
      to: `${scheduleRoot}/${competition.id}`,
      text: competitionName,
    })
  }

  const team = event.team
  const teamName = nameOf(team)
  if (team?.id && teamName) {
    crumbs.push({
      id: team.id,
      to: `${teamRoot}/${team.id}`,
      text: teamName,
    })
  }

  return crumbs
}
